import { useParams, useSearchParams } from "react-router-dom";
import { BrandDetail } from "./BrandDetail";
import { AppearanceSettings } from "@/types/brand";
import { useMemo, useState, useEffect } from "react";
import { useBrandTheme } from "@/theme/useBrandTheme";

/**
 * Minimal preview page for appearance settings
 * This page is designed to be loaded in an iframe for preview purposes
 * It accepts appearance settings via URL query parameters and postMessage
 */
export function AppearancePreview() {
  const { username } = useParams<{ username: string }>();
  const [searchParams] = useSearchParams();
  const [settingsFromMessage, setSettingsFromMessage] =
    useState<Partial<AppearanceSettings> | null>(null);

  // Parse appearance settings from URL query params (initial load)
  const initialSettings = useMemo<Partial<AppearanceSettings>>(() => {
    const settings: Partial<AppearanceSettings> = {};

    // Parse all appearance settings from query params
    const joinText = searchParams.get("joinText");
    const joinTitle = searchParams.get("joinTitle");
    const ambassadorText = searchParams.get("ambassadorText");
    const theme = searchParams.get("theme");
    const socialMediaIconSizes = searchParams.get("socialMediaIconSizes");
    const primayColour = searchParams.get("primayColour");
    const secondaryColour = searchParams.get("secondaryColour");
    const imageIds = searchParams.get("imageIds");
    const showcaseProductIds = searchParams.get("showcaseProductIds");

    if (joinText !== null) settings.joinText = joinText;
    if (joinTitle !== null) settings.joinTitle = joinTitle;
    if (ambassadorText !== null) settings.ambassadorText = ambassadorText;
    if (theme !== null) settings.theme = theme as any;
    if (socialMediaIconSizes !== null)
      settings.socialMediaIconSizes = socialMediaIconSizes as any;
    if (primayColour !== null) settings.primayColour = primayColour;
    if (secondaryColour !== null) settings.secondaryColour = secondaryColour;
    if (imageIds !== null) {
      try {
        settings.imageIds = JSON.parse(imageIds);
      } catch {
        settings.imageIds = [];
      }
    }
    if (showcaseProductIds !== null) {
      try {
        settings.showcaseProductIds = JSON.parse(showcaseProductIds);
      } catch {
        settings.showcaseProductIds = [];
      }
    }

    return settings;
  }, [searchParams]);

  // Listen for postMessage updates from parent
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // The settings tab embeds this page same-origin; accept nothing else.
      if (event.origin !== window.location.origin) return;

      if (event.data && event.data.type === "APPEARANCE_SETTINGS_UPDATE") {
        setSettingsFromMessage(event.data.settings);
      }
    };

    window.addEventListener("message", handleMessage);

    // Tell the parent this listener now exists. Anything posted before this
    // point was dropped, so the parent re-sends the current draft on this
    // signal — timers and iframe `load` both fire before React mounts here.
    // Opened directly (not embedded), window.parent is this window and the
    // message is a harmless self-post of an unhandled type.
    window.parent.postMessage(
      { type: "APPEARANCE_PREVIEW_READY" },
      window.location.origin
    );

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Use settings from postMessage if available, otherwise use initial settings
  const appearanceSettings = settingsFromMessage || initialSettings;

  // Live-apply the draft colors so brand-* tokens preview the picked values.
  useBrandTheme(appearanceSettings, true);

  return (
    <div style={{ minHeight: "100vh", width: "100%" }}>
      <BrandDetail
        isEmbedded={true}
        brandId={username || null}
        appearanceSettings={appearanceSettings}
      />
    </div>
  );
}
